import { useAppScrollRef } from '@doltech/core/lib/appScroll/withAppScroll';
import { routingUrls } from '@doltech/core/lib/routing/routing.config';
import { IconArrowsArrowLeft } from '@doltech/icons/IconArrowsArrowLeft';
import { IconArrowsArrowRight } from '@doltech/icons/IconArrowsArrowRight';
import { IconOtherMenuGridO } from '@doltech/icons/IconOtherMenuGridO';
import { Button } from '@doltech/ui/lib/figma/ButtonV2/ButtonV2';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Link } from '@doltech/ui/lib/figma/Link';
import { smoothScroll } from '@doltech/utils/lib/smooth-scroll';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { ModalPortalLayoutBottomSheet } from '@doltech/ui/lib/figma/Modal/ModalPortal/layouts/ModalPortalLayoutBottomSheet';
import { DolPopover } from '@doltech/ui/lib/figma/Popover/DolPopover';
import { shadows } from '@doltech/ui/lib/figma/shadows';
import { useGlobalModalPortalStore } from '@doltech/ui/lib/hocs/withGlobalModalPortal';
import { useFromScreens } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { flexGap, verticalSpace } from '@doltech/utils/lib/css-style';
import { OverallHeading } from '../../components/OverallHeading';
import { QuestionPalletes } from '../../components/QuestionPalletes';
import { useTestResultStore } from '../../model/test-result.hook';

const QuestionPalletesStyled = styled(QuestionPalletes)`
  flex-direction: column;
`;

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  .right-side {
    display: flex;
    align-items: center;
    ${flexGap(12)}
  }

  .left-side {
    display: flex;
    align-items: center;
    ${flexGap(12)}
  }
`;

const QuestionContent = styled.div`
  box-shadow: ${shadows.zIndex4};

  background: ${colorsV2.white100};
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;

  width: 100%;

  .header {
    padding: 14px 16px;
  }
  .body {
    padding: 14px 16px;
    ${verticalSpace(8)}
  }
`;

interface BottomNavigationMobileProps {
  audioPlayer?: React.ReactNode;
}

export const BottomNavigationMobile = observer((props: BottomNavigationMobileProps) => {
  const { audioPlayer } = props;
  const { appScrollRef } = useAppScrollRef();
  const [, from769] = useFromScreens([0, 769]);

  const store = useTestResultStore();

  const { show, hide } = useGlobalModalPortalStore();

  const showQuestion = () => {
    show({
      content: (
        <ModalPortalLayoutBottomSheet className="modal-student-rating" onOverlayClick={hide}>
          <QuestionContent>
            <div className="header">
              <OverallHeading title="Danh sách câu hỏi" onClose={hide} />
            </div>
            <div className="body">
              <QuestionPalletesStyled customStore={store} onHide={hide} />
              <Link
                rel="noreferrer"
                target="_blank"
                href={`${routingUrls.landing.ROOT_PATH}${store.seoVocabUrl}`}
              >
                <Button.Primary fullWidth>Xem danh sách từ vựng</Button.Primary>
              </Link>
            </div>
          </QuestionContent>
        </ModalPortalLayoutBottomSheet>
      ),
    });
  };

  return (
    <Container>
      <div className="left-side">
        {audioPlayer}
        {from769 ? (
          <DolPopover
            placement="top"
            overlayClassName="un-styled no-arrow custom-inner-content"
            maxWidth="max-content"
            trigger="click"
            content={
              <QuestionContent>
                <div className="header">
                  <OverallHeading title="Danh sách câu hỏi" />
                </div>
                <div className="body">
                  <QuestionPalletesStyled customStore={store} />
                  <Link
                    rel="noreferrer"
                    target="_blank"
                    href={`${routingUrls.landing.ROOT_PATH}${store.seoVocabUrl}`}
                  >
                    <Button.Primary fullWidth>Xem danh sách từ vựng</Button.Primary>
                  </Link>
                </div>
              </QuestionContent>
            }
          >
            <Button.Icon aria-label="All questions" type="secondary">
              <IconOtherMenuGridO />
            </Button.Icon>
          </DolPopover>
        ) : (
          <Button.Icon
            aria-label="All questions"
            onClick={(e) => {
              e.preventDefault();
              showQuestion();
            }}
            type="secondary"
          >
            <IconOtherMenuGridO />
          </Button.Icon>
        )}
      </div>

      <div className="right-side">
        <Button.Icon
          aria-label="Previous"
          onClick={(e) => {
            e.preventDefault();
            if (store.preventUserAction()) return;
            store.prev();
            smoothScroll(300, appScrollRef.current, 0);
          }}
          type="secondary"
          disabled={!store.canPrev}
        >
          <IconArrowsArrowLeft />
        </Button.Icon>
        <Button.Icon
          aria-label="Next"
          onClick={(e) => {
            e.preventDefault();
            if (store.preventUserAction()) return;
            store.next();
            smoothScroll(300, appScrollRef.current, 0);
          }}
          type="secondary"
          disabled={!store.canNext}
        >
          <IconArrowsArrowRight />
        </Button.Icon>
      </div>
    </Container>
  );
});
